/**
 * App bootstrap data access (AGENTS.md §2: UI modules must not call
 * supabase.from() directly). Serves the sign-time provisioning reads/writes in
 * AppAuth.tsx, the AppRealtime reload-flag poll, and AuthedApp's user-ticket
 * fetch — with the exact same tables, predicates, and limits as the former
 * inline calls.
 */
import { supabase } from '../supabase';

/** Fetch the admin_config globalAdmins row (single). */
export function fetchGlobalAdminsConfig() {
  return supabase.from('admin_config').select('data').eq('id', 'config').maybeSingle();
}

/** Fetch the tenant's automation configs (bounded 50 — boot-path read). */
export function fetchAutomationConfigsForBootstrap(tenantId: string | null | undefined) {
  return supabase.from('automation_configs').select('*').eq('tenant_id', tenantId).limit(50);
}

/** Fetch one tenant's data JSONB (single). */
export function fetchTenantDataRow(tenantId: string | null | undefined) {
  return supabase.from('tenants').select('data').eq('id', tenantId).maybeSingle();
}

/**
 * Fetch one provisioned profile row (users or guests) by tenant + id.
 * @param tableName 'users' | 'guests' — decided by the caller's role logic.
 */
export function fetchProvisionedProfile(tableName: string, tenantId: string | null | undefined, userId: string) {
  return supabase.from(tableName).select('*').eq('tenant_id', tenantId).eq('id', userId).maybeSingle();
}

/**
 * Upsert one provisioned profile row (last-login/name/email updates).
 * @param tableName 'users' | 'guests' — decided by the caller's role logic.
 */
export function upsertProvisionedProfile(tableName: string, payload: Record<string, unknown>) {
  return supabase.from(tableName).upsert(payload);
}

/** Fetch one system_config reload row (single). */
export function fetchSystemReloadRow(tenantId: string | null | undefined) {
  return supabase.from('system_config').select('reload_flag').eq('tenant_id', tenantId).eq('id', 'reload').maybeSingle();
}

/** Fetch the signed-in user's own tickets (newest first, bounded 50). */
export function fetchOwnSupportTickets(tenantId: string | null | undefined, createdBy: string) {
  return supabase.from('support_tickets').select('*')
    .eq('tenant_id', tenantId)
    .eq('created_by', createdBy || 'unknown')
    .order('created_at', { ascending: false })
    .limit(50);
}
